import React, { useState, useEffect } from 'react';
import { FaTimes, FaHistory, FaCalendar, FaClock, FaCheckCircle, FaExclamationTriangle, FaPause, FaEdit, FaEnvelope } from 'react-icons/fa';
import { adminService } from '../../services/adminService';

const ActionHistoryModal = ({ customer, onClose }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('all');

    useEffect(() => {
        loadHistory();
    }, [customer.UID]);

    const loadHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await adminService.getCustomerActionHistory(customer.UID, customer.email);
            setHistory(data.actions || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getActionIcon = (type) => {
        switch (type) {
            case 'approve': return <FaCheckCircle className="text-green-600" />;
            case 'reject': return <FaTimes className="text-red-600" />;
            case 'on_hold': return <FaPause className="text-orange-600" />;
            case 'update': return <FaEdit className="text-blue-600" />;
            case 'service_activation': return <FaCalendar className="text-purple-600" />;
            case 'email_sent': return <FaEnvelope className="text-indigo-600" />;
            default: return <FaHistory className="text-[#bdae93]" />;
        }
    };

    const formatActionDetails = (action) => {
        const d = action.actionDetails || {};
        const parts = [];
        if (d.approvalNotes) parts.push(`Notes: ${d.approvalNotes}`);
        if (d.rejectionNotes) parts.push(`Reason: ${d.rejectionNotes}`);
        if (d.holdNotes) parts.push(`Notes: ${d.holdNotes}`);
        if (d.serviceActivationDate) parts.push(`Activation: ${new Date(d.serviceActivationDate).toLocaleDateString()}`);
        if (d.subject) parts.push(`Email: ${d.subject}`);
        return parts.length > 0 ? parts.join(' • ') : 'No details';
    };

    const filteredHistory = filterType === 'all'
        ? history
        : history.filter(h => h.actionType === filterType);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-[#282828] rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
                <div className="p-6 border-b border-[#3c3836] flex-shrink-0 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-[#ebdbb2]">Action History</h2>
                    <button onClick={onClose} className="text-[#928374] hover:text-[#bdae93]">
                        <FaTimes size={24} />
                    </button>
                </div>

                <div className="p-4 border-b border-[#3c3836] bg-[#1d2021]">
                    <div className="flex items-center space-x-3">
                        <span className="text-sm text-[#bdae93]">Filter:</span>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="bg-[#3c3836] text-[#ebdbb2] text-sm rounded px-2 py-1 border border-[#504945] outline-none"
                        >
                            <option value="all">All Actions</option>
                            <option value="approve">Approvals</option>
                            <option value="reject">Rejections</option>
                            <option value="update">Updates</option>
                            <option value="email_sent">Emails</option>
                        </select>
                        <button onClick={loadHistory} className="ml-auto text-[#fabd2f] text-sm hover:underline">
                            Refresh
                        </button>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto flex-1 bg-[#1d2021]">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#fabd2f]"></div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500 text-center py-4">{error}</div>
                    ) : filteredHistory.length === 0 ? (
                        <div className="text-center py-10 text-[#7c6f64]">
                            <FaHistory className="mx-auto mb-3 text-4xl opacity-50" />
                            <p>No history found.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredHistory.map((action, i) => (
                                <div key={i} className="bg-[#282828] p-4 rounded-lg border border-[#3c3836]">
                                    <div className="flex items-start space-x-3">
                                        <div className="mt-1">{getActionIcon(action.actionType)}</div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start">
                                                <h4 className="text-[#ebdbb2] font-semibold capitalize">
                                                    {action.actionType.replace('_', ' ')}
                                                </h4>
                                                <span className="text-xs text-[#7c6f64]">
                                                    {new Date(action.timestamp).toLocaleString()}
                                                </span>
                                            </div>
                                            <p className="text-sm text-[#bdae93] mt-1">{formatActionDetails(action)}</p>
                                            <p className="text-xs text-[#7c6f64] mt-2">By: {action.adminUser || 'System'}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActionHistoryModal;
